<template>
  <v-app-bar
    id="app-bar"
    app
    elevation="0"
    height="64"
    class="app-bar-clean"
  >
    <!-- Menu Toggle Button -->
    <v-btn
      icon
      class="menu-toggle mr-2"
      @click="setDrawer(!drawer)"
    >
      <v-icon color="#212121" size="22">
        {{ drawer ? 'mdi-menu-open' : 'mdi-menu' }}
      </v-icon>
    </v-btn>

    <!-- App Title/Route Name -->
    <div class="app-title-section">
      <v-toolbar-title class="app-title">
        <div class="d-flex align-center">
          <div>
            <div class="title-main">HRVEC</div>
            <div class="title-sub">{{ formatRouteName($route.name) }}</div>
          </div>
        </div>
      </v-toolbar-title>
    </div>

    <v-spacer />

    <!-- Notification Bell -->
    <v-menu offset-y left v-model="notifMenuOpen" :close-on-content-click="false">
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          icon
          class="notification-btn mr-2"
          v-bind="attrs"
          v-on="on"
          :loading="loading"
        >
          <v-badge
            :content="notificationItems.length"
            :value="notificationItems.length > 0"
            color="error"
            overlap
          >
            <v-icon color="#212121" size="22">mdi-bell-outline</v-icon>
          </v-badge>
        </v-btn>
      </template>
      <v-card class="notification-card" min-width="380">
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-bell</v-icon>
          ศูนย์แจ้งเตือน
          <v-spacer></v-spacer>
          <v-chip small color="white" text-color="primary">{{ notificationItems.length }}</v-chip>
        </v-card-title>
        <v-list dense>
          <v-list-item
            v-for="item in notificationItems"
            :key="item.key"
            @click="handleNotificationClick(item)"
          >
            <v-list-item-avatar>
              <v-icon :color="item.color">{{ item.icon }}</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>{{ item.title }}</v-list-item-title>
              <v-list-item-subtitle>{{ item.detail }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-chip x-small :color="item.color" dark>{{ item.badge }}</v-chip>
            </v-list-item-action>
          </v-list-item>
          <v-divider v-if="notificationItems.length === 0"></v-divider>
          <v-list-item v-if="notificationItems.length === 0">
            <v-list-item-content class="text-center">
              <v-icon size="48" color="grey lighten-2">mdi-bell-off</v-icon>
              <div class="text-subtitle-1 grey--text">ไม่มีการแจ้งเตือน</div>
            </v-list-item-content>
          </v-list-item>
        </v-list>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="goToNotificationCenter">
            ดูทั้งหมดที่เมนูหลัก
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>

    <!-- User Profile Menu -->
    <v-menu offset-y left>
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          class="user-profile-btn mr-2"
          v-bind="attrs"
          v-on="on"
          text
        >
          <v-avatar size="36" class="mr-2" color="#f5f5f5">
            <v-img
              v-if="userProfile.avatar"
              :src="userProfile.avatar"
              alt="Profile"
            ></v-img>
            <v-icon v-else color="#b3151b" size="20">mdi-account-circle</v-icon>
          </v-avatar>
          <div class="user-info d-none d-md-block">
            <div class="user-name">{{ userProfile.name || 'ผู้ใช้งาน' }}</div>
            <div class="user-role">{{ userProfile.role || 'Admin' }}</div>
          </div>
          <v-icon color="#9e9e9e" class="ml-1">mdi-chevron-down</v-icon>
        </v-btn>
      </template>
      
      <v-card min-width="280">
        <v-card-text class="text-center pa-4">
          <v-avatar size="64" class="mb-3">
            <v-img
              v-if="userProfile.avatar"
              :src="userProfile.avatar"
              alt="Profile"
            ></v-img>
            <v-icon v-else color="primary" size="40">mdi-account-circle</v-icon>
          </v-avatar>
          <div class="text-h6 font-weight-bold">{{ userProfile.name || 'ผู้ใช้งาน' }}</div>
          <div class="text-subtitle-2 grey--text">{{ userProfile.email || 'user@example.com' }}</div>
          <v-chip small :color="userProfile.status === 'online' ? 'success' : 'grey'" class="mt-2">
            <v-icon left small>{{ userProfile.status === 'online' ? 'mdi-circle' : 'mdi-circle-outline' }}</v-icon>
            {{ userProfile.status === 'online' ? 'ออนไลน์' : 'ออฟไลน์' }}
          </v-chip>
        </v-card-text>
        
        <v-divider></v-divider>
        
        <v-list>            
          <v-list-item @click="goToSettings">
            <v-list-item-icon>
              <v-icon color="primary">mdi-cog</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>ตั้งค่า</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          
        </v-list>
      </v-card>
    </v-menu>

    <!-- Navigation Buttons -->
    <div class="nav-buttons">
      <v-btn
        class="nav-btn mr-1"
        text
        :to="'/admin/Dashboard'"
        exact
      >
        <v-icon left color="#212121">mdi-view-dashboard-outline</v-icon>
        <span class="d-none d-lg-inline nav-btn-text">Dashboard</span>
      </v-btn>

      <v-btn
        class="nav-btn mr-2"
        text
        :to="'/admin/home_menu'"
        exact
      >
        <v-icon left color="#212121">mdi-apps</v-icon>
        <span class="d-none d-lg-inline nav-btn-text">เมนูระบบ</span>
      </v-btn>
    </div>

    <!-- Logout Button -->
    <v-btn
      text
      class="logout-btn ml-2"
      @click="showLogoutDialog = true"
    >
      <v-icon left color="#b3151b">mdi-logout</v-icon>
      <span class="d-none d-md-inline">ออกจากระบบ</span>
    </v-btn>

    <!-- Logout Confirmation Dialog -->
    <v-dialog v-model="showLogoutDialog" max-width="400">
      <v-card class="logout-dialog">
        <v-card-title class="warning white--text">
          <v-icon left color="white">mdi-logout</v-icon>
          ยืนยันการออกจากระบบ
        </v-card-title>
        
        <v-card-text class="pa-6">
          <div class="text-center">
            <v-icon size="64" color="warning" class="mb-4">mdi-help-circle-outline</v-icon>
            <div class="mb-2">คุณต้องการออกจากระบบหรือไม่?</div>
            <div class="grey--text">ข้อมูลที่ยังไม่ได้บันทึกอาจสูญหาย</div>
          </div>
        </v-card-text>
        
        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn
            text
            color="grey"
            @click="showLogoutDialog = false"
          >
            ยกเลิก
          </v-btn>
          <v-btn
            color="error"
            @click="confirmLogout"
            class="ml-2"
          >
            <v-icon left>mdi-logout</v-icon>
            ออกจากระบบ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Loading Overlay -->
    <v-overlay v-if="isLoggingOut" absolute>
      <v-progress-circular
        indeterminate
        size="64"
        color="white"
      ></v-progress-circular>
      <div class="text-center mt-4">
        <div class="white--text">กำลังออกจากระบบ...</div>
      </div>
    </v-overlay>
  </v-app-bar>
</template>

<script>
// Components
import { VHover, VListItem } from "vuetify/lib";

// Utilities
import { mapState, mapMutations } from "vuex";

export default {
  name: "DashboardUserAppBar",

  components: {
    AppBarItem: {
      render(h) {
        return h(VHover, {
          scopedSlots: {
            default: ({ hover }) => {
              return h(
                VListItem,
                {
                  attrs: this.$attrs,
                  class: {
                    "primary--text": !hover,
                    "white--text primary elevation-4": hover
                  },
                  props: {
                    activeClass: "primary white--text",
                    dark: hover,
                    link: true,
                    ...this.$attrs
                  }
                },
                this.$slots.default
              );
            }
          }
        });
      }
    }
  },

  props: {
    value: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    showLogoutDialog: false,
    isLoggingOut: false,
    isDark: false,
      ApiKey: "HRvec2021",

    // ศูนย์แจ้งเตือนผู้ดูแลระบบ — ข้อมูลจาก admin_notification_center.php
    notifMenuOpen: false,
    loading: false,
    notifCenter: {
      summary: {},
      periods: [],
      requests: []
    },
    // User profile data
    userProfile: {
      name: "ผู้ดูแลระบบ",
      email: "",
      role: "ผู้ดูแลระบบ",
      avatar: null, // Can be set to an image URL
      status: "online"
    }
  }),

  computed: {
    ...mapState(["drawer"]),

    attentionPeriods() {
      return (this.notifCenter.periods || []).filter(p => p.state === 'overdue' || p.state === 'should_open');
    },

    pendingRequests() {
      return (this.notifCenter.requests || []).filter(r => r.count > 0);
    },

    notificationItems() {
      const sevColor = { error: 'red darken-1', warning: 'orange darken-2', info: 'blue darken-1' };
      const requestItems = this.pendingRequests.map(r => ({
        key: 'req_' + r.key,
        title: r.title,
        detail: r.detail || '',
        route: r.route,
        color: sevColor[r.severity] || sevColor.info,
        icon: 'mdi-inbox-arrow-down',
        badge: r.count
      }));
      const periodItems = this.attentionPeriods.map(p => ({
        key: 'period_' + p.source + '_' + p.id,
        title: p.title,
        detail: p.state === 'overdue' ? ('เลยกำหนดปิด ' + (-p.days_to_stop) + ' วันแต่ยังเปิดอยู่') : 'ถึงกำหนดแล้วแต่ยังไม่เปิด',
        route: p.route,
        color: p.state === 'overdue' ? 'red darken-1' : 'orange darken-2',
        icon: 'mdi-calendar-alert',
        badge: p.state === 'overdue' ? 'เลยกำหนด' : 'ควรเปิด'
      }));
      return [...requestItems, ...periodItems];
    }
  },

  mounted() {
    this.loadUserProfile();
    this.loadNotificationCenter();
    this.isDark = this.$vuetify.theme.dark;
  },

  methods: {
    async loadNotificationCenter() {
      this.loading = true;
      try {
        const result = await this.$http.post("admin_notification_center.php", {
          ApiKey: this.ApiKey
        });
        if (result.data && result.data.status) {
          this.notifCenter = result.data;
        }
      } catch (error) {
        console.error("Error loading notification center", error);
      } finally {
        this.loading = false;
      }
    },

    ...mapMutations({
      setDrawer: "SET_DRAWER"
    }),

    formatRouteName(routeName) {
      if (!routeName) return 'ระบบจัดการ';
      
      const routeNames = {
        'Dashboard': 'แดชบอร์ด',
        'home_menu': 'เมนูหลัก',
        'personnel_temporary_notify': 'การแจ้งเตือนบุคลากร',
        'rate_work_std02': 'สถิตินักเรียนนักศึกษา',
        'personnel_etc': 'รายงานอัตรากำลัง',
        'temporarycollegeemreg': 'ประกาศรับสมัคร',
        'temporaryemployees': 'ผู้สมัครงาน'
      };
      
      return routeNames[routeName] || routeName;
    },

    loadUserProfile() {
      // Load user profile from session storage or API
      const userSession = JSON.parse(localStorage.getItem("user")) || {};
      if (userSession) {
        this.userProfile = {
          ...this.userProfile,
          name: userSession.user_firstname || userSession.user_name || this.userProfile.name,
          email: userSession.user_email || this.userProfile.email,
          role: userSession.user_status || this.userProfile.role,
        };
      }
    },

    handleNotificationClick(item) {
      this.notifMenuOpen = false;
      if (item.route) {
        this.$router.push(item.route).catch(() => {});
      }
    },

    goToNotificationCenter() {
      this.notifMenuOpen = false;
      this.$router.push('/admin/home_menu').catch(() => {});
    },

    goToProfile() {
      // Navigate to profile page
      this.$router.push('/admin/profile');
    },

    goToSettings() {
      // Navigate to settings page
      this.$router.push('/admin/setting');
    },

    toggleTheme() {
      this.isDark = !this.isDark;
      this.$vuetify.theme.dark = this.isDark;
      // Save theme preference
      localStorage.setItem('darkTheme', this.isDark);
    },

    async confirmLogout() {
      this.isLoggingOut = true;
      this.showLogoutDialog = false;
      
      try {
        // Simulate logout process
        await new Promise(resolve => setTimeout(resolve, 1500));        
        // Clear session data
        localStorage.clear();
        localStorage.removeItem('authToken');           
  
        this.$router.push("/");
      } catch (error) {
        console.error('Logout error:', error);
      } finally {
        this.isLoggingOut = false;
      }
    },

    // Legacy logout method for compatibility
    logout() {
      this.showLogoutDialog = true;
    }
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Prompt:wght@300;400;500;600;700&display=swap");

.app-bar-clean {
  background: #ffffff !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
  font-family: 'Prompt', sans-serif;
}

.menu-toggle {
  transition: background 0.2s ease;
}

.app-title-section {
  margin-left: 4px;
}

.app-title {
  font-family: 'Prompt', sans-serif;
}

.title-main {
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.2;
  color: #263238;
  letter-spacing: 0.02em;
}

.title-sub {
  font-size: 0.78rem;
  font-weight: 400;
  color: #9e9e9e;
}

.notification-btn {
  transition: background 0.2s ease;
}

.notification-card {
  border-radius: 12px !important;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
}

.user-profile-btn {
  border-radius: 10px !important;
  padding: 6px 12px !important;
  transition: background 0.2s ease;
}

.user-profile-btn:hover {
  background: #fafafa !important;
}

.user-info {
  text-align: left;
  margin-left: 4px;
  margin-right: 4px;
}

.user-name {
  font-size: 0.87rem;
  font-weight: 600;
  color: #212121;
  line-height: 1.2;
}

.user-role {
  font-size: 0.72rem;
  color: #9e9e9e;
  line-height: 1;
}

.profile-card {
  border-radius: 12px !important;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
}

.nav-buttons .nav-btn {
  border-radius: 8px !important;
  transition: background 0.2s ease;
}

.nav-btn-text {
  color: #212121;
}

.nav-buttons .nav-btn:hover {
  background: #fafafa !important;
}

.nav-buttons .nav-btn.v-btn--active {
  background: #fbecec !important;
}

.logout-btn {
  border-radius: 8px !important;
  transition: background 0.2s ease;
}

.logout-btn:hover {
  background: #fbecec !important;
}

.logout-dialog {
  border-radius: 15px !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .app-title-section {
    margin-left: 4px;
  }

  .title-main {
    font-size: 0.95rem;
  }

  .title-sub {
    display: none;
  }

  .user-profile-btn {
    padding: 4px 8px !important;
  }

  .nav-buttons {
    display: none;
  }
}

@media (max-width: 480px) {
  .notification-btn {
    margin-right: 4px !important;
  }

  .user-profile-btn {
    margin-right: 4px !important;
  }

  .logout-btn {
    margin-left: 8px !important;
    padding: 8px !important;
  }
}

/* Animation for notifications */
.v-badge >>> .v-badge__badge {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

/* Dark theme support */
.theme--dark .notification-card,
.theme--dark .profile-card,
.theme--dark .logout-dialog {
  background: #212121 !important;
}

/* Loading overlay styling */
.v-overlay >>> .v-overlay__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* Smooth transitions */
* {
  transition: all 0.3s ease;
}

/* Custom scrollbar for notification list */
.notification-card >>> .v-list {
  max-height: 300px;
  overflow-y: auto;
}

.notification-card >>> .v-list::-webkit-scrollbar {
  width: 4px;
}

.notification-card >>> .v-list::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.notification-card >>> .v-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.notification-card >>> .v-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>